import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { AccountType, Formula, PaymentTermsDates, paymentTermsDatesForAccountType } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { BehaviorSubject, of } from 'rxjs'
import { FormulaPricingApiService, type FormulaPricingPreviewFormula } from 'src/api/formula-pricing'
import { isUsdaProposalOnDealEnabled } from 'src/components/deals-list/formula-pricing'
import { incompleteFormula, usdaPanelState$, UsdaProposalPanelComponent, type UsdaDealLine } from 'src/components/usda-proposal-panel'
import { FormulaFormComponent, FormulaFormField, isUsdaPriceSource } from 'src/shared/formula-form'

export interface MatchedOfferFormulaDialogData {
  formula: DeepReadonly<Formula>
  currency: string
  measureId: string
  /** buyer incoterm/location, used only to seed a line that has none of its own */
  defaultIncoterm: string
  defaultLocation: string
  buyerId: number
  productName: string
  /** the buyer index the differential is quoted against - fixed, shown for context only */
  indexPrice: number | null
  /** WA-22777: the deal line the MO is on, if any (buyer side) */
  dealLine?: UsdaDealLine | null
}

export interface MatchedOfferFormulaDialogResult {
  /** null: "Use proposed price" switched the buyer side to a flat price (WA-22777) */
  formula: Formula | null
  flatPrice: number | null
}

/**
 * WA-22298: lets a trader retune the buyer side of a formula MO - days, before date,
 * differential, incoterm, incoterm location and conditions. Product name, price source and the
 * index stay pinned to the index the deal was bought on.
 */
@Component({
  selector: 'tc-matched-offer-formula-dialog',
  templateUrl: './matched-offer-formula-dialog.component.html',
  styleUrls: ['./matched-offer-formula-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    FormulaFormComponent,
    UsdaProposalPanelComponent,
  ],
})
export class MatchedOfferFormulaDialogComponent {
  private readonly dialogData = inject<MatchedOfferFormulaDialogData>(MAT_DIALOG_DATA)
  private readonly dialogRef = inject<MatDialogRef<MatchedOfferFormulaDialogComponent, MatchedOfferFormulaDialogResult>>(MatDialogRef)
  private readonly FormulaPricingApi = inject(FormulaPricingApiService)

  constructor() {
    this.currentFormula = { ...this.dialogData.formula } as Formula
  }

  // Dialog data
  protected readonly formula = this.dialogData.formula
  protected readonly currency = this.dialogData.currency
  protected readonly measureId = this.dialogData.measureId
  protected readonly defaultIncoterm = this.dialogData.defaultIncoterm
  protected readonly defaultLocation = this.dialogData.defaultLocation
  protected readonly buyerId = this.dialogData.buyerId
  protected readonly productName = this.dialogData.productName
  protected readonly indexPrice = this.dialogData.indexPrice
  protected readonly dealLine = this.dialogData.dealLine ?? null

  // WA-22777: USDA proposal for the formula on screen (live preview when the MO is on a deal)
  private readonly formula$ = new BehaviorSubject<Formula | null>(this.dialogData.formula as Formula)
  protected readonly usda$ = isUsdaProposalOnDealEnabled()
    ? usdaPanelState$({
      formula$: this.formula$,
      preview: this.dealLine
        ? formula => this.FormulaPricingApi.preview(this.dealLine.dealId, {
          side: 'buyer',
          product_index: this.dealLine.productIndex,
          formula: formula as FormulaPricingPreviewFormula,
        })
        : null,
      nightly: this.dealLine?.entry,
      visible: isUsdaPriceSource(this.dialogData.formula?.price_source),
    })
    : of(null)

  protected readonly readonlyFields: FormulaFormField[] =
    ['indexPrice', 'productName', 'priceSource']

  protected readonly paymentTermsDates: PaymentTermsDates = paymentTermsDatesForAccountType(AccountType.BUYER)

  // State
  protected readonly isValid$ = new BehaviorSubject<boolean>(false)

  /** the formula on screen, for the USDA panel's not-current check */
  protected currentFormula: Formula
  private currentFlatPrice: number | null = null

  protected onFormulaChange(formula: Formula | null) {
    if (formula) this.currentFormula = formula
    this.formula$.next(formula)
  }

  protected onValidityChange(isValid: boolean) {
    this.isValid$.next(isValid)
    if (!isValid) this.formula$.next(incompleteFormula(this.formula$.value))
  }

  /** WA-22777: switch the buyer side to flat at the proposal – no formula, the proposal as the Price (buyerEstPrice) */
  protected useProposedPrice(price: number) {
    this.dialogRef.close({ formula: null, flatPrice: price })
  }

  protected onFlatPriceChange(flatPrice: number | null) {
    this.currentFlatPrice = flatPrice
  }

  protected save() {
    if (!this.isValid$.value) return

    this.dialogRef.close({ formula: this.currentFormula, flatPrice: this.currentFlatPrice })
  }

  protected cancel() {
    this.dialogRef.close(undefined)
  }
}
